import React, { useEffect, useRef } from 'react';
import HelloSign from 'hellosign-embedded';
import moment from 'moment-timezone';
import axios from 'axios';

// Desktop bounds for the signing frame, taken from the embedded client's own
// modal stylesheet (.x-hellosign-embedded__modal-content is 80vw / min 512px /
// max 1024px). MIN_FRAME_HEIGHT matches the iframe's own min-height, below which
// the signing UI gets clipped rather than scrolled.
const MAX_FRAME_WIDTH = 1024;
const MIN_FRAME_WIDTH = 512;
const MIN_FRAME_HEIGHT = 400;

/**
 * ContractPopup Component
 * 
 * A full-screen popup that displays a contract URL using HelloSign embedded for unsigned contracts
 * or an iframe for signed contracts.
 * 
 * @param {Object} props - Component props
 * @param {string} props.url - The URL of the contract to display
 * @param {boolean} props.isSigned - Whether the contract is already signed
 * @param {string} props.signatureRequestId - Dropbox signature request being signed, used to persist the processing state
 * @param {function} props.onClose - Function to call when the popup is closed
 * @returns {JSX.Element} - The rendered component
 */
const ContractPopup = ({ url, isSigned = false, onClose , elections, setElections, signatureRequestId}) => {
  const containerRef = useRef(null);
  const helloSignClientRef = useRef(null);

  // Initialize HelloSign client
  useEffect(() => {
    if (!url || !containerRef.current || isSigned) return;
    
    // Create a new HelloSign client instance
    const client = new HelloSign();
    helloSignClientRef.current = client;
    
    try {
      // No width/height here on purpose - the embedded client has no such
      // options (its whole config surface is clientId, container, allowCancel,
      // debug, skipDomainVerification, testMode, timeout, allowViewportOverride,
      // redirectTo, hideHeader, requestingEmail, whiteLabeling, locale). The
      // iframe is height:100%/min-width:100%, so the container div below is what
      // actually sizes it - see handleResize.
      client.open(url, {
        clientId: 'a661969607523d9ea11f424a037a14a8',
        container: containerRef.current,
        allowCancel: true,
        // Must match how the signature request was created on the backend - the
        // embedded client refuses to load a test_mode request unless this is true,
        // and a live one unless it is false.
        testMode: false,

        // // Mobile optimization options
        // uxVersion: 2, // Use the latest UX version
        // hideHeader: isMobile, // Hide header on mobile for more space
        
        // // Add event handlers
        // messageListener: (eventData) => {
        //   if (eventData.event === 'sign_complete' || eventData.event === 'decline_complete' || eventData.event === 'close') {
        //     onClose();
        //   }
        // }
      });
      client.on('sign', (data) => {
        // Handle the completion of the signing process
        console.log('Signing completed:', data);
        // The signature is in, but Dropbox hasn't generated the signed PDF yet -
        // go to 'Processing', not 'Signed', so the card doesn't offer a download
        // that would 4xx. ContractSection polls from here until it's ready.
        setElections((prevElections) => {
          const newElections = [...prevElections];
          if (newElections[0]?.dj_elections?.[0]) {
            newElections[0].dj_elections[0].dropbox_contract_status = 'Processing';
            newElections[0].dj_elections[0].election_status = 'Contract Signed';
            newElections[0].dj_elections[0].dropbox_contract_signed_date = moment().tz('America/Los_Angeles').format('MM-DD-YYYY');
          }
          return newElections;
        });
        // Persist it too. The signature_request_signed webhook writes the same
        // status, but it can trail the browser - without this, a DJ who refreshes
        // right away is shown the unsigned card and re-offered this iframe.
        if (signatureRequestId) {
          axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/dropbox/markContractProcessing`, {
            signature_request_id: signatureRequestId
          }).catch((error) => {
            // The webhook is the backstop, so a failure here only costs us the
            // refresh-proofing - nothing the DJ needs to act on.
            console.error('Error marking contract as processing:', error);
          });
        }
        onClose();
      });
    } catch (error) {
      console.error('Error initializing HelloSign:', error);
    }
    
    // Cleanup function
    return () => {
      if (helloSignClientRef.current) {
        try {
          // Close the HelloSign client if it's open
          helloSignClientRef.current.close();
          helloSignClientRef.current = null;
        } catch (error) {
          console.error('Error closing HelloSign client:', error);
        }
      }
    };
  }, [url, isSigned, onClose, setElections, signatureRequestId]);

  // Handle window resize
  useEffect(() => {
    if (!containerRef.current || isSigned) return;
    
    const handleResize = () => {
      // Adjust container dimensions on resize
      if (containerRef.current) {
        const isMobile = window.innerWidth < 768;

        // The container is border-box and carries p-1/md:p-4, so its padding
        // comes out of the iframe's height:100%. Measure it and add it back, or
        // the signing frame ends up smaller than the size computed below.
        const styles = window.getComputedStyle(containerRef.current);
        const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
        const paddingY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);

        const availableWidth = window.innerWidth - 80;
        const availableHeight = window.innerHeight - 80;

        if (isMobile) {
          // On mobile, use full width
          containerRef.current.style.width = '100%';
          containerRef.current.style.height = `${availableHeight + paddingY}px`;
        } else {
          // Size the way HelloSign sizes its own modal (80vw, 512-1024px wide,
          // 90vh tall, 400px floor). The embedded UI is a toolbar and field
          // navigation wrapped around the document, not a bare letter page - the
          // 8.5/11 ratio this used to use starved that chrome, leaving ~630px of
          // width on a 1440px screen.
          const frameWidth = Math.max(Math.min(availableWidth, MAX_FRAME_WIDTH), MIN_FRAME_WIDTH);
          const frameHeight = Math.max(availableHeight, MIN_FRAME_HEIGHT);

          containerRef.current.style.width = `${frameWidth + paddingX}px`;
          containerRef.current.style.height = `${frameHeight + paddingY}px`;
        }
      }
    };
    
    // Initial sizing
    handleResize();
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isSigned]);

  if (!url) return null;

  // Determine if we're on mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center overflow-hidden">
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={() => {
            // Ensure HelloSign client is properly closed before calling onClose
            if (helloSignClientRef.current && !isSigned) {
              try {
                helloSignClientRef.current.close();
              } catch (error) {
                console.error('Error closing HelloSign on button click:', error);
              }
              helloSignClientRef.current = null;
            }
            onClose();
          }}
          className="bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors duration-200"
          aria-label="Close contract popup"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-6 w-6 text-gray-700" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M6 18L18 6M6 6l12 12" 
            />
          </svg>
        </button>
      </div>
      
      <div 
        ref={containerRef}
        className={`max-w-full max-h-full p-1 md:p-4 bg-white ${isMobile ? 'w-full' : ''}`}
        style={{ 
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          transform: 'translateZ(0)', // Force GPU acceleration for sharper rendering
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          overflow: 'hidden'
        }}
      >
        {/* For signed contracts, use an iframe instead of HelloSign */}
        {isSigned && (
          <iframe
            src={url}
            title="Signed Contract Document"
            className="w-full h-full border-0"
            style={{ height: '100%' }}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
          />
        )}
        {/* HelloSign will render in this div for unsigned contracts */}
      </div>
    </div>
  );
};

export default ContractPopup;

// Add a global function to trigger the contract popup from the console
if (typeof window !== 'undefined') {
  window.contract = {
    open: (url, isSigned = false) => {
      // Create a container for the popup if it doesn't exist
      let container = document.getElementById('contract-popup-container');
      if (!container) {
        container = document.createElement('div');
        container.id = 'contract-popup-container';
        document.body.appendChild(container);
      }
      
      // Render the popup using React
      const React = require('react');
      const ReactDOM = require('react-dom');
      const ContractPopup = require('./ContractPopup').default;
      
      ReactDOM.render(
        React.createElement(ContractPopup, {
          url,
          isSigned,
          onClose: () => {
            ReactDOM.unmountComponentAtNode(container);
          }
        }),
        container
      );
      
      console.log('Contract popup opened with URL:', url);
    },
    close: () => {
      const container = document.getElementById('contract-popup-container');
      if (container) {
        const ReactDOM = require('react-dom');
        ReactDOM.unmountComponentAtNode(container);
        console.log('Contract popup closed');
      }
    }
  };
}
